import React, { useState, useEffect , useRef } from "react";
import { useAuth } from "../../Auth/AuthContext";
import BASE_URL from "../../../config/config";
import DateDisplay from "../../common/DateDisplay";


const UserList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [csrfToken, setCsrfToken] = useState("");
  const { user: currentUser } = useAuth();

  // پارامترهای صفحه‌بندی
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // پارامترهای جستجو
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searching, setSearching] = useState(false);

  const searchInputRef = useRef(null);
  const wasFocused = useRef(false);

  // پارامترهای مرتب‌سازی
  const [sortField, setSortField] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");

  // دریافت توکن CSRF
  useEffect(() => {
    const fetchCsrfToken = async () => {
      try {
        const response = await fetch(`${BASE_URL}/csrf-token`, {
          credentials: "include",
        });
        const data = await response.json();
        setCsrfToken(data.csrfToken);
      } catch (error) {
        setError("خطا در دریافت توکن امنیتی");
      }
    };

    fetchCsrfToken();
  }, []);

  // دریافت لیست کاربران
  useEffect(() => {
    if (csrfToken) {
      fetchUsers();
    }
  }, [csrfToken, currentPage, searchQuery, sortField, sortDirection]); // searchInput حذف شده از وابستگی‌ها

  const fetchUsers = async () => {
    setLoading(true);
    try {
      // ساخت URL با پارامترهای جستجو، صفحه‌بندی و مرتب‌سازی
      let url = `${BASE_URL}/users?page=${currentPage}&limit=${itemsPerPage}`;

      if (searchQuery) {
        url += `&search=${encodeURIComponent(searchQuery)}`;
      }

      url += `&sortField=${sortField}&sortDirection=${sortDirection}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "CSRF-Token": csrfToken,
        },
        credentials: "include",
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || "خطا در دریافت اطلاعات");
      }

      const data = await response.json();

      // بررسی ساختار داده‌های دریافتی
      if (Array.isArray(data)) {
        // API قدیمی که آرایه برمی‌گرداند
        setUsers(data);
        setTotalItems(data.length);
        setTotalPages(1);
      } else {
        // API جدید با ساختار صفحه‌بندی
        setUsers(data.users || []);
        setTotalItems(data.total || 0);
        setTotalPages(data.totalPages || 1);
      }

      setLoading(false);
      setSearching(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
      setSearching(false);
    }
  };

  // تغییر ترتیب مرتب‌سازی
  const handleSort = (field) => {
    if (sortField === field) {
      // اگر روی همان ستون کلیک شد، جهت مرتب‌سازی را عوض کن
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // اگر روی ستون دیگری کلیک شد، فیلد را تغییر بده و جهت را به پیش‌فرض برگردان
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // نمایش آیکون مرتب‌سازی
  const renderSortIcon = (field) => {
    if (sortField !== field) {
      return <i className="fas fa-sort sort-icon"></i>;
    }
    return sortDirection === "asc" ? (
      <i className="fas fa-sort-up sort-icon active"></i>
    ) : (
      <i className="fas fa-sort-down sort-icon active"></i>
    );
  };

  // مدیریت تغییر متن در فیلد جستجو - بدون ارسال درخواست
  const handleSearchInputChange = (e) => {
    wasFocused.current = document.activeElement === e.target;
    setSearchInput(e.target.value);
  };

  // این useEffect را اضافه کنید - برای حفظ فوکوس
  useEffect(() => {
    if (wasFocused.current && searchInputRef.current) {
      const selectionStart = searchInputRef.current.selectionStart;
      const selectionEnd = searchInputRef.current.selectionEnd;
      
      // تاخیر کوچک برای اطمینان از اینکه DOM به‌روزرسانی شده است
      setTimeout(() => {
        searchInputRef.current.focus();
        searchInputRef.current.setSelectionRange(selectionStart, selectionEnd);
      }, 0);
    }
  }, [searchInput]);

  // جستجوی کاربران - فقط با کلیک دکمه جستجو
  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput);
    setCurrentPage(1); // بازگشت به صفحه اول نتایج
    setSearching(true);
  };

  // پاک کردن جستجو
  const clearSearch = () => {
    setSearchInput("");
    setSearchQuery("");
    setCurrentPage(1);
  };

  // حذف منطقی کاربر
  const handleDeleteUser = async (userId) => {
    if (!window.confirm("آیا از حذف این کاربر اطمینان دارید؟")) return;

    try {
      const response = await fetch(`${BASE_URL}/users/${userId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "CSRF-Token": csrfToken,
        },
        credentials: "include",
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || "خطا در حذف کاربر");
      }

      // بروزرسانی لیست با حفظ صفحه فعلی
      fetchUsers();
      alert("کاربر با موفقیت حذف شد");
    } catch (err) {
      setError(err.message);
      alert(`خطا در حذف کاربر: ${err.message}`);
    }
  };

  // تغییر صفحه
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // ویرایش کاربر
  const handleEditUser = (userId) => {
    window.location.href = `/admin/users/edit/${userId}`;
  };

  // افزودن کاربر جدید
  const handleAddUser = () => {
    window.location.href = "/admin/users/add";
  };

  // کامپوننت صفحه‌بندی
  const Pagination = () => {
    const pageNumbers = [];

    // محاسبه دامنه صفحات نمایشی
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);

    // اضافه کردن صفحه اول
    if (startPage > 1) {
      pageNumbers.push(1);
      if (startPage > 2) pageNumbers.push("...");
    }

    // اضافه کردن صفحات میانی
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    // اضافه کردن صفحه آخر
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) pageNumbers.push("...");
      pageNumbers.push(totalPages);
    }

    return (
      <div className="pagination">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="pagination-btn"
        >
          قبلی
        </button>

        {pageNumbers.map((number, index) =>
          number === "..." ? (
            <span key={`dots-${index}`} className="pagination-dots">
              ...
            </span>
          ) : (
            <button
              key={number}
              onClick={() => handlePageChange(number)}
              className={`pagination-btn ${
                currentPage === number ? "active" : ""
              }`}
            >
              {number}
            </button>
          )
        )}

        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="pagination-btn"
        >
          بعدی
        </button>
      </div>
    );
  };

  // کامپوننت جستجو
  const SearchBox = () => {
    return (
      <div className="search-container">
        <form onSubmit={handleSearch}>
          <div className="search-box">
            <input
              type="text"
              ref={searchInputRef}
              placeholder="جستجو بر اساس شناسه، نام کاربری، ایمیل یا شماره تماس..."
              value={searchInput}
              onChange={handleSearchInputChange}
              className="search-input"
            />
            <div className="search-buttons">
              <button
                type="submit"
                className="search-btn"
                disabled={searching || loading}
              >
                {searching ? "در حال جستجو..." : "جستجو"}
              </button>
              {searchQuery && (
                <button
                  type="button"
                  className="clear-search-btn"
                  onClick={clearSearch}
                >
                  پاک کردن
                </button>
              )}
            </div>
          </div>
        </form>
        {searchQuery && (
          <div className="search-info">
            نتایج جستجو برای: "{searchQuery}"{" "}
            {totalItems === 0
              ? "(نتیجه‌ای یافت نشد)"
              : `(${totalItems} مورد یافت شد)`}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="user-list-container">
      <div className="user-list-header">
        <h1>لیست کاربران</h1>
        <button className="add-user-btn" onClick={handleAddUser}>
          <i className="fas fa-plus"></i> افزودن کاربر جدید
        </button>
      </div>

      {/* کامپوننت جستجو */}
      <SearchBox />

      {loading ? (
        <div className="loading">در حال بارگذاری...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : users.length === 0 ? (
        <div className="no-records">هیچ کاربری یافت نشد</div>
      ): (
        <>
          <div className="table-responsive">
            <table className="user-table">
              <thead>
                <tr>
                  <th
                    onClick={() => handleSort("id")}
                    className="sortable-header"
                  >
                    شناسه {renderSortIcon("id")}
                  </th>
                  <th>تصویر</th>
                  <th
                    onClick={() => handleSort("username")}
                    className="sortable-header"
                  >
                    نام کاربری {renderSortIcon("username")}
                  </th>
                  <th
                    onClick={() => handleSort("email")}
                    className="sortable-header"
                  >
                    ایمیل {renderSortIcon("email")}
                  </th>
                  <th>شماره تماس</th>
                  <th>نقش</th>
                  <th
                    onClick={() => handleSort("created_at")}
                    className="sortable-header"
                  >
                    تاریخ ثبت نام {renderSortIcon("created_at")}
                  </th>
                  <th
                    onClick={() => handleSort("updated_at")}
                    className="sortable-header"
                  >
                    آخرین بروزرسانی {renderSortIcon("updated_at")}
                  </th>
                  <th>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {users.length > 0 ? (
                  users.map((user) => (
                    <tr key={user.id}>
                      <td>{user.id}</td>
                      <td>
                        <img
                          src={user.profile_pic || "/images/avatar.jpg"}
                          alt={user.username}
                          className="user-avatar"
                        />
                      </td>
                      <td>{user.username}</td>
                      <td>{user.email}</td>
                      <td>{user.phone || "---"}</td>
                      <td>
                        <span className={`role-badge ${user.role}`}>
                          {user.role === "admin" ? "مدیر" : "کاربر"}
                        </span>
                      </td>
                      <td>
                        <DateDisplay isoDate={user.created_at} />
                      </td>
                      <td>
                        <DateDisplay isoDate={user.updated_at} />
                      </td>
                      <td className="actions">
                        <button
                          className="edit-btn"
                          onClick={() => handleEditUser(user.id)}
                          title="ویرایش"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button
                          className="delete-btn"
                          onClick={() => handleDeleteUser(user.id)}
                          title="حذف"
                          disabled={user.id === currentUser?.id}
                        >
                          <i className="fas fa-trash-alt"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="no-data">
                      {searchQuery
                        ? "هیچ نتیجه‌ای برای جستجوی شما یافت نشد"
                        : "هیچ کاربری یافت نشد"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* نمایش صفحه‌بندی */}
          {totalPages > 1 && <Pagination />}

          {/* نمایش اطلاعات صفحه‌بندی */}
          {totalItems > 0 && (
            <div className="pagination-info">
              نمایش {(currentPage - 1) * itemsPerPage + 1} تا{" "}
              {Math.min(currentPage * itemsPerPage, totalItems)} از {totalItems}{" "}
              مورد
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UserList;
